import { updatingTrueTime } from '../actions/internalActions';
import setAccountsEditorState from '../mutators/setAccountsEditorState';
import getAccountsEditorState from '../selectors/getAccountsEditorState';
import { AccountsEditorState } from '../store/schema/AccountsEditorState';
import { accountConnected } from 'owa-accounts-service';
import { disconnectOWAAccount } from 'owa-accounts-service';
import {
    getOWAConnectedAccountsForProvider,
    OWAConnectedAccountState,
    getOWAConnectedAccount,
} from 'owa-accounts-store';
import { isTrueTimeEnabledForProvider, getAccountAddressForMergedView } from 'owa-accounts-utils';
import { orchestrator } from 'satcheljs';
import {
    lazyGetMergeViewEnabledAccount,
    lazyRemoveMergedViewAvailabilityAccount,
    lazyAddMergedViewAvailabilityAccount,
} from 'owa-truetime';
import { logUsage } from 'owa-analytics';
import * as trace from 'owa-trace';
import getAccountsEditorSource from '../selectors/getAccountsEditorSource';

/**
 * Listens to when account is connected and enables trutime for it
 * Also takes care of re-connect scenario by removing the invalid account of same provider type
 */
orchestrator(accountConnected, async ({ userIdentity, accountUniqueId, accountProviderType }) => {
    const accountsEditorSource = getAccountsEditorSource();
    let isTrueTimeEnabled = false;
    let wasTrueTimeAutoFixAttempted = false;
    let isReconnectFlow = false;

    let getMergeViewEnabledAccount = await lazyGetMergeViewEnabledAccount.import();
    const mergeViewEnabledAccount = getMergeViewEnabledAccount();

    if (mergeViewEnabledAccount) {
        // We don't want to enable TrueTime later on if it's already enabled
        isTrueTimeEnabled = true;

        if (
            mergeViewEnabledAccount !=
            getAccountAddressForMergedView(getOWAConnectedAccount(userIdentity))
        ) {
            // true time can only be enabled for one account at a time and it's already enabled for a different account.
            // Attempt to auto-fix this scenario by disabling true time for the other account so we can enabled it for this account.
            // Auto-disabling true time during account add is a fail safe and ideally this state should not happen.
            wasTrueTimeAutoFixAttempted = true;

            // Disable true time if it's already enabled for any other account
            const removeMergedViewAvailabilityAccount = await lazyRemoveMergedViewAvailabilityAccount.import();
            const updateSuccessful = await removeMergedViewAvailabilityAccount(
                accountProviderType,
                accountsEditorSource
            );
            isTrueTimeEnabled = !updateSuccessful; // TrueTime is enabled if the removal failed
        }
    }

    // take care of reconnect scenario where user entered a different account in the auth popup
    // in which case, we add that account and remove all other accounts of the same providertype that are not in valid state
    const accountsForProvider = getOWAConnectedAccountsForProvider(accountProviderType);
    accountsForProvider.map(async account => {
        if (
            account.userIdentity != userIdentity &&
            account.accountState != OWAConnectedAccountState.Valid
        ) {
            isReconnectFlow = true;
            disconnectOWAAccount(account, accountsEditorSource, true /* re-connect flow */);
        }
    });

    // if this event is being fired that means users are still ending up in bad account / true time states.
    if (wasTrueTimeAutoFixAttempted) {
        logUsage(
            'trueTimeAutoFixAttemptedOnAccountConnected',
            {
                autoFixSuccess_1: !isTrueTimeEnabled, // the auto-fix was successful if true time is disabled
                isReconnectFlow_2: isReconnectFlow,
            },
            {
                isCore: true,
            }
        );

        trace.errorThatWillCauseAlert(
            `TrueTime was already enabled for another account and was auto-disabled ${
                isTrueTimeEnabled ? 'unsccessfully' : 'successfully'
            }.  isReconnectFlow: ${isReconnectFlow}`
        );
    }

    // now trigger enable trutime for the newly added account if it is not already enabled
    if (isTrueTimeEnabledForProvider(accountProviderType) && !isTrueTimeEnabled) {
        // Turning on true time by default, show the spinner
        updatingTrueTime(userIdentity);

        // Turning on true time
        let addMergedView = await lazyAddMergedViewAvailabilityAccount.import();
        await addMergedView(
            userIdentity,
            accountUniqueId,
            accountProviderType,
            accountsEditorSource
        );

        // True time is turned on, so hiding the spinner
        updatingTrueTime(null);
    }

    // if AccountsEditorState is "Adding", that means there were no failures during the account addition process
    // hence change it now to "Added"
    if (getAccountsEditorState() == AccountsEditorState.Adding) {
        setAccountsEditorState(AccountsEditorState.Added);
    }
});
